import styled, { WithTheme } from '@emotion/styled';
import { IconPosition, StyledProps as Props, Theme } from './TextArea.types';
import { css } from '@emotion/core';
import { SerializedStyles } from '@emotion/serialize';
import { variant } from 'styled-system';
import { getHighlighted } from '../Input/Input.styled';

export const getStateColors = () => ({
  default: 'var(--color-indigo-t5)',
  success: 'var(--color-vibrant-blue-t8)',
  error: 'var(--color-coral)',
  warning: 'var(--color-amber)',
  filled: 'var(--color-indigo-t11)',
  highlighted: 'var(--color-lime)',
});

const base = (p: WithTheme<Props, Theme>): SerializedStyles =>
  variant({
    prop: 'mode',
    variants: {
      primary: {
        background: 'var(--color-extra-white)',
        borderRadius: '4px',
        border: '1px solid var(--color-indigo-t3)',
        outline: 'none',
        color: 'var(--color-indigo-t11)',
        width: '100%',
        '&:hover': {
          border: `1px solid var(--color-indigo-t2)`,
          boxShadow: `0px 2px 4px rgba(123, 97, 255, 0.15)`,
        },
        '&:focus-within': {
          border: `2px solid var(--color-lime)`,
          background: 'var(--color-extra-white)',
          boxShadow: `0px 2px 4px rgba(221, 243, 63, 0.15)`,
        },
      },
      secondary: {
        background: 'var(--color-extra-white)',
        borderBottom: '1px solid var(--color-indigo-t3)',
        outline: 'none',
        color: 'var(--color-indigo-t11)',
        width: '100%',
        '&:hover': {
          borderBottom: `1px solid var(--color-indigo-t2)`,
        },
        '&:focus-within': {
          borderBottom: `1px solid var(--color-lime)`,
          background: 'var(--color-extra-white)',
          boxShadow: `0px 2px 4px rgba(221, 243, 63, 0.15)`,
        },
      },
    },
  });

const filled = (p: WithTheme<Props, Theme>): SerializedStyles =>
  p.value
    ? css({
        color: 'var(--color-indigo-t11)',
        borderColor: 'var(--color-indigo-t11)',
      })
    : null;

const states = (p: WithTheme<Props, Theme>): SerializedStyles =>
  variant({
    prop: 'state',
    variants: {
      success: {
        borderColor: 'var(--color-vibrant-blue-t8)',
      },
      error: {
        borderColor: 'var(--color-coral)',
      },
      warning: {
        borderColor: 'var(--color-amber)',
      },
      filled: {
        color: 'var(--color-indigo-t11)',
        borderColor: 'var(--color-indigo-t11)',
      },
      highlighted: getHighlighted(p.mode),
    },
  });

export const TextAreaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1 0;
  align-items: center;
  min-width: 0;
`;

export const TextArea = styled.textarea`
  flex: 1 0;
  min-width: 20px;
  min-height: 28px;
  font-size: inherit;
  background-color: transparent;
  border: 0;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: transparent;
  }

  &[disabled]:hover {
    background-color: transparent;
  }

  ${(p: WithTheme<Props, Theme>) => p.expandable && css({ resize: p.expandable, zIndex: 2 })}

  ${(p: WithTheme<Props, Theme>) => p.autoResize && css({ resize: 'none' })}

  ${(p: WithTheme<Props, Theme>) =>
    css({
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: p.theme.sizes.font.base,
      color: 'var(--color-indigo-t11)',
      '&::placeholder': {
        color: 'var(--color-indigo-t2)',
      },
    })}

  ${(p: WithTheme<Props, Theme>) =>
    variant({
      prop: 'mode',
      variants: {
        primary: {
          minHeight: '50px',
          padding: '16px 12px',
          fontFamily: p.theme.fonts.secondary,
          '&:disabled': {
            opacity: 1,
            WebkitTextFillColor: 'var(--color-indigo-t5)',
            backgroundColor: 'transparent',
            color: 'var(--color-indigo-t1)',
          },
        },
        secondary: {
          lineHeight: p.theme.lineHeights[7],
          paddingTop: '2px',
          minHeight: '28px',
          fontFamily: p.theme.fonts.primary,
          '&:disabled': {
            opacity: 1,
            WebkitTextFillColor: 'var(--color-indigo-t5)',
            backgroundColor: 'transparent',
            color: 'var(--color-indigo-t1)',
          },
        },
      },
    })}
`;

export const IconContainer = styled.div`
  position: relative;
`;

export const Icon = styled.div<any>`
  flex: 0 0;
  display: flex;
  align-items: center;
  position: absolute;

  ${(p) =>
    p.iconPosition === IconPosition.LEFT
      ? variant({
          prop: 'mode',
          variants: {
            primary: {
              top: '10px',
              position: 'relative',
              alignSelf: 'flex-start',
              paddingRight: '6px',
              left: '12px',
            },
            secondary: {
              top: '3px',
              position: 'relative',
              alignSelf: 'flex-start',
              paddingRight: '2px',
            },
          },
        })
      : variant({
          prop: 'mode',
          variants: {
            primary: {
              top: '10px',
              right: '12px',
            },
            secondary: {
              right: '12px',
              alignSelf: 'flex-start',
            },
          },
        })}
`;

export const Container = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  ${base}

  ${filled}

  ${states}

  ${(p) =>
    p.disabled
      ? variant({
          prop: 'mode',
          variants: {
            primary: {
              border: '1px solid var(--color-indigo-t1)',
              background: 'var(--color-b0)',
              '&:hover': {
                boxShadow: 'none',
                border: '1px solid  var(--color-indigo-t1)',
              },
            },
            secondary: {
              borderBottom: '1px solid var(--color-indigo-t3)',
              '&:hover': {
                boxShadow: 'none',
                borderBottom: '1px solid var(--color-indigo-t3)',
              },
            },
          },
        })
      : null}
`;
